import {
  ApostleFilterInput,
  ApostleLite,
  apostleLiteSchema,
  ApostleSortBy
} from '@apeiron/apostle'
import { DEFAULT_PAGE_INFO, SortInput } from '@apeiron/common'
import {
  deserializeListData,
  deserializePageInfo,
  PageInfo,
  PageInput,
  serializePagingInput
} from '@apeiron/temp'
import { useLazyQuery } from '@apollo/client'
import graphql from '@src/graphql/query/getMyApostles'
import * as R from 'ramda'
import { useCallback, useState } from 'react'

const useGetMyApostles = (): Hook => {
  const [query, { loading }] = useLazyQuery(graphql)

  const [data, setData] = useState<ApostleLite[]>([])

  const [pageInfo, setPageInfo] = useState<PageInfo>(DEFAULT_PAGE_INFO)

  const fetch = useCallback(
    async (request: GetMyApostlesRequestInput): Promise<Response> => {
      const {
        bookmarked = false,
        filterInput = {},
        pageInput = {},
        sortInput = { sortBy: ApostleSortBy.IDAsc }
      } = request

      const response = await query({
        variables: {
          bookmarked,
          filterInput: R.reject(R.isNil, filterInput),
          pagingInput: serializePagingInput(pageInput),
          sortInput
        }
      })

      const apostles = deserializeListData<ApostleLite>(
        response,
        apostleLiteSchema
      )

      const pageInfo = deserializePageInfo(response, pageInput)

      setData(apostles)

      setPageInfo(pageInfo)

      return {
        data: apostles,
        pageInfo
      }
    },
    [query]
  )

  return {
    data,
    loading,
    pageInfo,
    fetch
  }
}

export type GetMyApostlesRequestInput = {
  bookmarked?: boolean
  filterInput?: ApostleFilterInput
  pageInput?: PageInput
  sortInput?: SortInput<ApostleSortBy>
}

type Response = {
  data: ApostleLite[]
  pageInfo: PageInfo
}

type Hook = {
  data: ApostleLite[]
  fetch: (request: GetMyApostlesRequestInput) => Promise<Response>
  loading: boolean
  pageInfo: PageInfo
}

export default useGetMyApostles
